import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { api, formatApiError } from '@/App';
import { Envelope, Plus, PencilSimple, Trash, Eye, Code } from '@phosphor-icons/react';

const INTERNAL_STATUSES = ['Any', 'New Service Request', 'Quotation Accepted', 'Site Visit Scheduled',
                           'Assigned to Agent', 'Query Logged', 'Under Review', 'Approved', 'Completed'];

const TEMPLATE_VARIABLES = [
  { key: '{{client_name}}', desc: 'Client/company name' },
  { key: '{{company_name}}', desc: 'Company name' },
  { key: '{{case_ref}}', desc: 'Case reference ID' },
  { key: '{{case_status}}', desc: 'New internal status' },
  { key: '{{public_status}}', desc: 'Public-facing status' },
  { key: '{{previous_status}}', desc: 'Previous status' },
  { key: '{{service_type}}', desc: 'Service type' },
  { key: '{{consultant_name}}', desc: 'Assigned consultant' },
  { key: '{{consultant_email}}', desc: 'Consultant email' },
];

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', subject: '', body_html: '',
    trigger_from_status: 'Any', trigger_to_status: '', is_active: true
  });

  const fetchTemplates = async () => {
    try {
      const { data } = await api.get('/email-templates');
      setTemplates(data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const resetForm = () => {
    setForm({ name: '', subject: '', body_html: '', trigger_from_status: 'Any', trigger_to_status: '', is_active: true });
    setEditing(null);
  };

  const openCreate = () => { resetForm(); setDialogOpen(true); };

  const openEdit = (tpl) => {
    setEditing(tpl.template_id);
    setForm({
      name: tpl.name, subject: tpl.subject, body_html: tpl.body_html,
      trigger_from_status: tpl.trigger_from_status || 'Any',
      trigger_to_status: tpl.trigger_to_status || '',
      is_active: tpl.is_active
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.subject || !form.body_html || !form.trigger_to_status) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      if (editing) {
        await api.put(`/email-templates/${editing}`, form);
        toast.success('Template updated');
      } else {
        await api.post('/email-templates', form);
        toast.success('Template created');
      }
      setDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/email-templates/${id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleToggle = async (tpl) => {
    try {
      await api.put(`/email-templates/${tpl.template_id}`, { is_active: !tpl.is_active });
      fetchTemplates();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const insertVariable = (varKey) => {
    setForm(prev => ({ ...prev, body_html: prev.body_html + varKey }));
  };

  if (loading) {
    return <AdminLayout title="Email Templates"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-[#4CAF50] border-t-transparent rounded-full animate-spin"></div></div></AdminLayout>;
  }

  return (
    <AdminLayout title="Email Templates">
      <div className="space-y-6 fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A192F]">Email Templates</h1>
            <p className="text-sm text-gray-500 mt-1">Manage automated email notifications for case status changes</p>
          </div>
          <Button onClick={openCreate} className="bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none" data-testid="create-template-btn">
            <Plus className="w-4 h-4 mr-2" /> New Template
          </Button>
        </div>

        <div className="grid gap-4">
          {templates.map((tpl) => (
            <Card key={tpl.template_id} data-testid={`template-card-${tpl.template_id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Envelope className="w-5 h-5 text-[#4CAF50]" />
                      <h3 className="font-semibold text-[#0A192F]">{tpl.name}</h3>
                      <Badge className={tpl.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                        {tpl.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">Subject: {tpl.subject}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Badge variant="outline">{tpl.trigger_from_status || 'Any'}</Badge>
                      <span>-&gt;</span>
                      <Badge variant="outline" className="border-[#4CAF50] text-[#4CAF50]">{tpl.trigger_to_status}</Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={tpl.is_active} onCheckedChange={() => handleToggle(tpl)} data-testid={`toggle-${tpl.template_id}`} />
                    <Button variant="ghost" size="icon" onClick={() => { setPreviewHtml(tpl.body_html); setPreviewOpen(true); }} data-testid={`preview-${tpl.template_id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(tpl)} data-testid={`edit-${tpl.template_id}`}>
                      <PencilSimple className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(tpl.template_id)} data-testid={`delete-${tpl.template_id}`}>
                      <Trash className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {templates.length === 0 && (
            <Card><CardContent className="p-12 text-center text-gray-500">No email templates yet. Create one to get started.</CardContent></Card>
          )}
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Template' : 'Create Email Template'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Template Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Case Under Review" className="rounded-none" data-testid="template-name-input" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Trigger: From Status</Label>
                  <Select value={form.trigger_from_status} onValueChange={(v) => setForm({ ...form, trigger_from_status: v })}>
                    <SelectTrigger className="rounded-none" data-testid="trigger-from-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{INTERNAL_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Trigger: To Status *</Label>
                  <Select value={form.trigger_to_status} onValueChange={(v) => setForm({ ...form, trigger_to_status: v })}>
                    <SelectTrigger className="rounded-none" data-testid="trigger-to-select"><SelectValue placeholder="Select status..." /></SelectTrigger>
                    <SelectContent>{INTERNAL_STATUSES.filter(s => s !== 'Any').map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Email Subject *</Label>
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Your case {{case_ref}} has been updated" className="rounded-none" data-testid="template-subject-input" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Email Body (HTML) *</Label>
                  <div className="flex items-center gap-1">
                    <Code className="w-4 h-4 text-gray-400" />
                    <span className="text-xs text-gray-400">Insert variables:</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {TEMPLATE_VARIABLES.map(v => (
                    <button key={v.key} type="button" onClick={() => insertVariable(v.key)}
                      className="px-2 py-1 text-xs bg-gray-100 hover:bg-[#4CAF50]/10 text-gray-600 hover:text-[#4CAF50] border border-gray-200 transition-colors"
                      title={v.desc}
                    >
                      {v.key}
                    </button>
                  ))}
                </div>
                <Textarea value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} rows={10} className="rounded-none font-mono text-sm" data-testid="template-body-input" />
              </div>

              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label>Active</Label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">Cancel</Button>
                <Button onClick={handleSave} className="bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none" data-testid="save-template-btn">
                  {editing ? 'Update Template' : 'Create Template'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Email Preview</DialogTitle></DialogHeader>
            <div className="mt-4 p-6 bg-gray-50 border border-gray-200 rounded" dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default EmailTemplates;
